import { useEffect, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { MarketplaceLogo } from 'src/components/common/MarketplaceLogo';
import { useContent } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { MARKETPLACE_TO_BACKGROUND_COLOR } from 'src/utils/constants/colorMaps';
import { ContentId } from 'src/utils/constants/contentId';
import { getCurrentLanguage } from 'src/utils/localeUtils';
import { formatPercent } from 'src/utils/numberFormatter';
import { Marketplace } from 'src/WebApiController';

import { DashboardSectionWrapper } from '../../DashboardSectionWrapper';
import { ChartHeaderInput } from '../ChartHeader';
import * as styles from '../DataInsightsChart.css';
import { SaleChartData, useDataInsights } from '../DataInsightsContext';
import { NoData } from '../NoData';
import { TimePeriodSelector } from '../TimePeriodSelector';
import { MarketshareTooltip } from './MarketshareTooltip';

export const marketplaceMapping: { [id: number]: Marketplace } = Object.values(
  Marketplace
).reduce(
  (acc, marketplace, index) => {
    acc[index + 1] = marketplace;
    return acc;
  },
  {} as { [id: number]: Marketplace }
);

export const MarketshareChart = ({
  data,
  renderHeader,
}: {
  data: SaleChartData[] | null;
  renderHeader: (input: ChartHeaderInput) => JSX.Element;
}) => {
  const quantityNumberFormat = new Intl.NumberFormat(getCurrentLanguage(), {
    useGrouping: true,
  });
  const { uiCurrency, isScrolling, setIsScrolling, isLoading } =
    useDataInsights();
  const soldTicketsText = useContent(ContentId.SoldTickets);
  const [currMarketshare, setCurrMarketshare] = useState<SaleChartData | null>(
    null
  );
  useEffect(() => {
    if (data && data.length > 0) {
      setCurrMarketshare(data[data.length - 1]);
    }
  }, [data]);

  const { totalQuantity, marketshare } = currMarketshare || {};

  const marketplaceKeys = currMarketshare ? Object.keys(marketshare || {}) : [];

  const filteredMarketshare = Object.entries(marketshare || {}).reduce(
    (acc, [key, value]) => {
      if (!value || key === '0') return acc;
      if (key) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <>
      {renderHeader({
        value: currMarketshare?.totalRevenue || 0,
        prefix: uiCurrency.sym,
        isScrolling,
        subtitle: `${quantityNumberFormat.format(
          totalQuantity || 0
        )} ${soldTicketsText}`,
      })}
      {isLoading ? (
        <div className={styles.loadingSpinner}>
          <PosSpinner />
        </div>
      ) : !data || data.length === 0 ? (
        <NoData />
      ) : (
        <DashboardSectionWrapper>
          <ResponsiveContainer width="100%" height={450}>
            <AreaChart
              data={data}
              stackOffset="expand"
              onMouseLeave={() => {
                setIsScrolling(false);
                setCurrMarketshare(data?.[data.length - 1]);
              }}
              onMouseEnter={() => {
                setIsScrolling(true);
              }}
            >
              <YAxis domain={['dataMin', 'dataMax']} hide />

              {marketplaceKeys.map((key) => {
                const marketplaceId = Number(key);
                const marketplace = marketplaceMapping[marketplaceId];
                if (!marketplace) return null; // Skip if key doesn't map to a valid marketplace

                const color =
                  MARKETPLACE_TO_BACKGROUND_COLOR[marketplace] || '#cccccc'; // Fallback color

                return (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={`marketshare.${marketplaceId}`}
                    stackId="1"
                    stroke={color}
                    fill={color}
                    fillOpacity={1}
                    name={marketplace}
                    activeDot={false}
                  />
                );
              })}
              <Tooltip
                isAnimationActive={false}
                cursor={{
                  stroke: vars.color.backgroundPrimary,
                  strokeWidth: 1,
                }}
                position={{
                  y: 20,
                }}
                content={(props) => {
                  return (
                    <MarketshareTooltip
                      label={props.label}
                      payload={props.payload}
                      setCurrMarketshare={setCurrMarketshare}
                    />
                  );
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </DashboardSectionWrapper>
      )}
      <TimePeriodSelector />

      <DashboardSectionWrapper fixed>
        <Stack style={{ padding: '20px 0', overflowX: 'auto' }}>
          {marketshare &&
            Object.entries(filteredMarketshare).map((item, index) => {
              const marketplaceId = item[0];
              const value = item[1];

              return (
                <>
                  {index !== 0 && <div className={styles.verticalDivider} />}
                  <Stack
                    direction={'column'}
                    key={index}
                    alignItems="center"
                    style={{
                      margin: '0 20px',
                      gap: '4px',
                      ...(index === 0 ? { marginLeft: 0 } : {}),
                    }}
                  >
                    <span style={{ fontSize: vars.typography.fontSize.lg }}>
                      {formatPercent(value)}
                    </span>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      gap={'m'}
                    >
                      <div
                        style={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          backgroundColor:
                            MARKETPLACE_TO_BACKGROUND_COLOR[
                              marketplaceMapping[Number(marketplaceId)]
                            ],
                        }}
                      />
                      <MarketplaceLogo
                        showTooltip={true}
                        marketplace={marketplaceMapping[Number(marketplaceId)]}
                        logoDimensions={{ width: '20px', height: '20px' }}
                        variant="square"
                      />
                    </Stack>
                  </Stack>
                </>
              );
            })}
        </Stack>
      </DashboardSectionWrapper>
    </>
  );
};
